/**
 * Coverage — the front door of the Compliance section (IA spec §3).
 *
 * Answers: what does this product assess, partially assess, and structurally not assess —
 * for this deployment, against the standard, at requirement-part granularity.
 *
 * Three rules carried from the spec, each pinned by a test:
 *   1. The coverage percentage NEVER renders without its denominator and per-class
 *      breakdown — a bare "40% assessed" lies in both directions.
 *   2. "Not assessed" always renders with its reason. A part without a reason reads as an
 *      omission; with one it reads as scope.
 *   3. A load failure renders as a failure — never as an empty table that could read as
 *      "nothing is assessed" (or worse, as clean).
 *
 * The Export link serves the backend's own HTML (boundary statement included) rather than
 * printing this page — the export must be the artifact whose honesty rules are tested
 * server-side, not a screenshot.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { errorDetail } from '../services/api';
import { fmtTs } from '../utils/complianceFormat';

// Fifth chip (2026-08-20 review): "in reach, adapter not yet implemented" is
// build queue, not scope boundary — rendering both as gray "Not assessed"
// undersold the honest headline.
const IN_REACH_STYLE = { label: 'In reach — not yet implemented', cls: 'bg-sky-900/40 text-sky-300 border-sky-700' };

const CLASS_STYLES = {
  assessed: { label: 'Assessed', cls: 'bg-green-900/40 text-green-300 border-green-700' },
  assessed_with_required_input: { label: 'Assessed with required input', cls: 'bg-amber-900/40 text-amber-300 border-amber-700' },
  partially_assessed: { label: 'Partially assessed', cls: 'bg-blue-900/40 text-blue-300 border-blue-700' },
  not_assessed: { label: 'Not assessed', cls: 'bg-gray-700/40 text-gray-300 border-gray-600' },
};

function ClassBadge({ coverageClass, reasonClass }) {
  const s = (coverageClass === 'not_assessed' && reasonClass === 'adapter_not_implemented')
    ? IN_REACH_STYLE
    : CLASS_STYLES[coverageClass] || { label: coverageClass, cls: 'bg-gray-700/40 text-gray-300 border-gray-600' };
  return (
    <span className={`inline-block px-2 py-0.5 text-[11px] rounded-full border whitespace-nowrap ${s.cls}`}>
      {s.label}
    </span>
  );
}

// The headline is composed SERVER-SIDE from the breakdown alone and rendered
// verbatim — the client must never recompute it (two computations of one
// quantity disagreed on one page; Aligned review, 2026-08-20).

export default function ComplianceCoverage() {
  const [coverage, setCoverage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Single-site by correction: Coverage speaks for ONE explicitly selected
  // site (default: most recently assessed, resolved server-side). The
  // five-site rollup was the multi-site false summary the IA deferred to v2.
  const [siteId, setSiteId] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.get('/skills/compliance/coverage'
        + (siteId ? `?site_id=${siteId}` : ''));
      if (!mounted.current) return;
      setCoverage(data);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'Coverage could not be loaded.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h1 className="text-2xl font-bold text-white">Assessment coverage</h1>
        {coverage && coverage.available_sites?.length > 1 && (
          <select data-testid="coverage-site-select"
                  value={coverage.site?.id || ''}
                  onChange={(e) => setSiteId(e.target.value)}
                  className="bg-gray-800 border border-gray-600 rounded-lg px-2 py-1.5 text-sm text-gray-200 mr-2">
            {coverage.available_sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        )}
        {coverage && (
          <a
            data-testid="coverage-export"
            href={`${api.defaults.baseURL}/skills/compliance/coverage.html${coverage.site ? `?site_id=${coverage.site.id}` : ''}`}
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800"
          >
            Export / share
          </a>
        )}
      </div>
      {coverage && (
        <p className="text-sm text-gray-400 mb-4">
          {coverage.standards.join(', ')}
          {coverage.site ? ` · Scope: ${coverage.site.name}` : ''} · as of {fmtTs(coverage.as_of)}
        </p>
      )}

      {loading && <div className="text-gray-400 text-sm">Loading coverage…</div>}

      {error && (
        <div data-testid="coverage-error" className="bg-red-900/30 border border-red-800 rounded-xl p-5 text-sm text-red-200">
          <div className="font-semibold mb-1">Coverage could not be loaded.</div>
          <div>{error} Nothing on this page should be read as a coverage claim until it loads.</div>
          <button onClick={load} className="mt-3 px-3 py-1.5 rounded-lg border border-red-700 hover:bg-red-900/40">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && coverage && (
        <>
          <div className="bg-gray-800 border-l-4 border-gray-500 rounded-r-xl p-4 mb-4 text-sm text-gray-300">
            <span className="font-semibold text-white">Scope of this system. </span>
            {coverage.boundary_statement}
          </div>

          <p data-testid="coverage-summary" className="text-base text-white mb-1">
            {coverage.summary.headline_sentence}
          </p>
          <p data-testid="coverage-trajectory" className="text-sm text-gray-300 mb-5">
            {coverage.summary.trajectory_sentence}
          </p>

          <div className="overflow-x-auto bg-gray-800 rounded-xl border border-gray-700">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-400 border-b border-gray-700">
                  <th className="px-4 py-3">Part</th>
                  <th className="px-4 py-3">Requirement (paraphrase)</th>
                  <th className="px-4 py-3">Coverage</th>
                  <th className="px-4 py-3">Reason / required input</th>
                  <th className="px-4 py-3">Second source</th>
                </tr>
              </thead>
              <tbody>
                {coverage.parts.map((p) => (
                  <tr key={p.part} data-testid={`part-${p.part}`} className="border-b border-gray-700/60 align-top">
                    <td className="px-4 py-3 font-mono text-xs text-gray-300 whitespace-nowrap">{p.part}</td>
                    <td className="px-4 py-3 text-gray-200">{p.title}</td>
                    <td className="px-4 py-3"><ClassBadge coverageClass={p.coverage_class} reasonClass={p.reason_class} /></td>
                    <td className="px-4 py-3 text-gray-400">
                      {p.reason || (p.coverage_class === 'assessed' ? '—' : '')}
                      {'queued' in p && (
                        <div data-testid={`queue-${p.part}`}
                             className={`mt-1 text-xs ${p.queued ? 'text-sky-300' : 'text-amber-300'}`}>
                          {p.queued
                            ? `Queued: ${p.queue_ref} · in reach since ${p.in_reach_since}`
                            : `Not yet queued · in reach since ${p.in_reach_since}`}
                        </div>
                      )}
                      {(p.required_inputs || []).length > 0 && (
                        <div className="mt-1 text-xs">
                          {p.required_inputs.map((u) => (
                            <div key={u.fact_kind} className="font-mono">
                              {u.fact_kind}: <span className="text-amber-300">{u.state}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{p.second_source || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-500 mt-4 italic">{coverage.percentage_note}</p>
        </>
      )}
    </div>
    </div>
  );
}
